import React, { useEffect, useState, useCallback } from "react";
import { api, apiErr, fmtMoney, API } from "../lib/api";
import { ROLE_LABELS } from "../lib/permissions";
import { PageHeader } from "../components/ResourcePage";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Link2, Unlink } from "lucide-react";
import { toast } from "sonner";

const BLANK = {
  user_id: "", name: "", role: "", email: "", phone: "", address: "", nin: "",
  bank_name: "", bank_account: "", id_file: "", pay_type: "hourly", pay_rate: 0,
  hire_date: "", active_override: null,
};

const PAY_TYPES = [{ value: "hourly", label: "Hourly" }, { value: "salary", label: "Salary" }];

export function Employees() {
  const [items, setItems] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [delId, setDelId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [e, s] = await Promise.all([api.get("/employees"), api.get("/staff-directory")]);
      setItems(e.data); setStaff(s.data);
    } catch (err) { toast.error(apiErr(err)); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // staff accounts available to link (exclude already-linked ones, keep current record's link)
  const linkedIds = new Set(items.map((i) => i.user_id).filter(Boolean));
  const accountOptions = staff.filter((s) => !linkedIds.has(s.user_id) || s.user_id === form.user_id);

  const openNew = () => { setForm(BLANK); setEditing(null); setOpen(true); };
  const openEdit = (row) => {
    setForm({
      user_id: row.user_id || "", name: row.name || "", role: row.role || "",
      email: row.email || "", phone: row.phone || "", address: row.address || "",
      nin: row.nin || "", bank_name: row.bank_name || "", bank_account: row.bank_account || "",
      id_file: row.id_file || "", pay_type: row.pay_type || "hourly", pay_rate: row.pay_rate || 0,
      hire_date: row.hire_date || "", active_override: row.active_override ?? null,
    });
    setEditing(row.id); setOpen(true);
  };

  const onPickAccount = (val) => {
    if (val === "none") {
      setForm((f) => ({ ...f, user_id: "" }));
      return;
    }
    const acc = staff.find((s) => s.user_id === val);
    setForm((f) => ({ ...f, user_id: val, name: acc?.name || f.name, role: acc?.role || f.role }));
  };

  const uploadFile = async (file) => {
    if (!file) return;
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, id_file: data.file_id }));
      toast.success("File uploaded");
    } catch (e) { toast.error(apiErr(e)); }
  };

  const save = async () => {
    if (!form.user_id && !form.name) return toast.error("Enter a name or link a staff account.");
    const payload = { ...form, pay_rate: Number(form.pay_rate || 0) };
    try {
      if (editing) await api.put(`/employees/${editing}`, payload);
      else await api.post("/employees", payload);
      toast.success(editing ? "Updated" : "Added");
      setOpen(false); load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const toggleActive = async (row, next) => {
    try {
      await api.patch(`/employees/${row.id}/active`, { active: next });
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const doDelete = async () => {
    try { await api.delete(`/employees/${delId}`); toast.success("Deleted"); setDelId(null); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const linkedAccount = staff.find((s) => s.user_id === form.user_id);

  return (
    <div className="fade-up">
      <PageHeader title="Employees" subtitle="HR records linked to staff accounts — Employee ID & name stay in sync with User Management."
        action={<Button onClick={openNew} data-testid="emp-add"><Plus className="mr-1.5 h-4 w-4" />Add</Button>} />

      <div className="rounded-lg border border-border bg-card overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Employee ID</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Account</TableHead>
              <TableHead>Phone</TableHead>
              <TableHead>Bank</TableHead>
              <TableHead className="text-right">Pay rate</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-20 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={9} className="py-10 text-center text-muted-foreground">Loading…</TableCell></TableRow>
            ) : items.length === 0 ? (
              <TableRow><TableCell colSpan={9} className="py-12 text-center text-muted-foreground">No employees yet. Click Add to get started.</TableCell></TableRow>
            ) : items.map((row) => (
              <TableRow key={row.id} data-testid="emp-row">
                <TableCell className="font-semibold">{row.name || "—"}</TableCell>
                <TableCell className="text-sm font-mono">{row.employee_id || "—"}</TableCell>
                <TableCell className="text-sm capitalize">{ROLE_LABELS[row.role] || (row.role || "—")}</TableCell>
                <TableCell>
                  {row.linked ? (
                    <Badge className="border-0 bg-sky-100 text-sky-700" data-testid="emp-linked-badge"><Link2 className="mr-1 h-3 w-3" />Linked</Badge>
                  ) : (
                    <Badge className="border-0 bg-slate-100 text-slate-500"><Unlink className="mr-1 h-3 w-3" />Unlinked</Badge>
                  )}
                </TableCell>
                <TableCell className="text-sm">{row.phone || "—"}</TableCell>
                <TableCell className="text-sm">{row.bank_name || "—"}</TableCell>
                <TableCell className="text-right num">{fmtMoney(row.pay_rate)}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Switch checked={!!row.active} onCheckedChange={(v) => toggleActive(row, v)} data-testid="emp-active-toggle" />
                    <Badge className={`border-0 ${row.active ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-500"}`} data-testid="emp-status-badge">
                      {row.active ? "Active" : "Inactive"}
                    </Badge>
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(row)} data-testid="emp-edit"><Pencil className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => setDelId(row.id)} data-testid="emp-delete"><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">{editing ? "Edit" : "New"} Employee</DialogTitle>
            <DialogDescription>Link to a staff account so the Employee ID and name stay consistent everywhere.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs">Staff account</Label>
              <Select value={form.user_id || "none"} onValueChange={onPickAccount}>
                <SelectTrigger className="mt-1.5" data-testid="emp-field-account"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None (unlinked)</SelectItem>
                  {accountOptions.map((s) => (
                    <SelectItem key={s.user_id} value={s.user_id}>
                      {s.name} {s.employee_id ? `· ${s.employee_id}` : ""} ({ROLE_LABELS[s.role] || s.role})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {linkedAccount && (
                <p className="mt-1.5 text-xs text-muted-foreground" data-testid="emp-linked-info">
                  Employee ID <b className="font-mono">{linkedAccount.employee_id || "—"}</b>, name and role come from this account.
                </p>
              )}
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label className="text-xs">Full name</Label>
                <Input className="mt-1.5" value={form.name} disabled={!!form.user_id}
                  onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="emp-field-name" />
              </div>
              <div>
                <Label className="text-xs">Role / Position</Label>
                <Input className="mt-1.5" value={form.role} disabled={!!form.user_id}
                  onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="emp-field-role" />
              </div>
              <div>
                <Label className="text-xs">Email</Label>
                <Input className="mt-1.5" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="emp-field-email" />
              </div>
              <div>
                <Label className="text-xs">Phone</Label>
                <Input className="mt-1.5" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="emp-field-phone" />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs">Address</Label>
                <Textarea className="mt-1.5" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="emp-field-address" />
              </div>
              <div>
                <Label className="text-xs">NIN</Label>
                <Input className="mt-1.5" value={form.nin} onChange={(e) => setForm({ ...form, nin: e.target.value })} data-testid="emp-field-nin" />
              </div>
              <div>
                <Label className="text-xs">Bank name</Label>
                <Input className="mt-1.5" value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} data-testid="emp-field-bank" />
              </div>
              <div>
                <Label className="text-xs">Bank account number</Label>
                <Input className="mt-1.5" value={form.bank_account} onChange={(e) => setForm({ ...form, bank_account: e.target.value })} data-testid="emp-field-account-no" />
              </div>
              <div>
                <Label className="text-xs">Pay type</Label>
                <Select value={form.pay_type} onValueChange={(v) => setForm({ ...form, pay_type: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="emp-field-paytype"><SelectValue /></SelectTrigger>
                  <SelectContent>{PAY_TYPES.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Pay rate (₦)</Label>
                <Input className="mt-1.5" type="number" value={form.pay_rate} onChange={(e) => setForm({ ...form, pay_rate: e.target.value })} data-testid="emp-field-payrate" />
              </div>
              <div>
                <Label className="text-xs">Hire date</Label>
                <Input className="mt-1.5" type="date" value={form.hire_date} onChange={(e) => setForm({ ...form, hire_date: e.target.value })} data-testid="emp-field-hiredate" />
              </div>
              <div>
                <Label className="text-xs">Status</Label>
                <Select
                  value={form.active_override === null || form.active_override === undefined ? "auto" : (form.active_override ? "active" : "inactive")}
                  onValueChange={(v) => setForm({ ...form, active_override: v === "auto" ? null : v === "active" })}>
                  <SelectTrigger className="mt-1.5" data-testid="emp-field-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">Auto (follow account)</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs">ID document / photo</Label>
                <div className="mt-1.5 flex items-center gap-3">
                  {form.id_file ? (
                    <img src={`${API}/files/${form.id_file}`} alt="" className="h-14 w-14 rounded-md border border-border object-cover" />
                  ) : (
                    <div className="flex h-14 w-14 items-center justify-center rounded-md border border-dashed border-border text-[10px] text-muted-foreground">None</div>
                  )}
                  <Input type="file" accept="image/*,application/pdf" className="text-xs" onChange={(e) => uploadFile(e.target.files[0])} data-testid="emp-field-idfile" />
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="emp-save">{editing ? "Save changes" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delId} onOpenChange={() => setDelId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this employee record?</AlertDialogTitle>
            <AlertDialogDescription>This removes the HR record only. The staff login account is not affected.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-destructive" data-testid="emp-confirm-delete">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default Employees;
